import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Textarea } from '@artosera/ui';
import { transitionAutorisee } from '@artosera/shared';
import { Heart } from 'lucide-react';
import { creerAffairePresentation } from '../../api/affairePresentation.js';
import { appelDeRequete, cleMetier, dansLaPortee } from '../../api/cles.js';
import { useMutation } from '../../api/mutation.js';
import { clesPresentation, type ChargePresentation } from '../../api/presentation.js';
import { useCreerReservation } from '../../api/reservations.js';
import type { SourcePresentation } from '../../api/sourcePresentation.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { NouvelleOffreDialog } from '../offres/NouvelleOffreDialog.js';
import { useEnLigne } from '../racine/BandeauHorsLigne.js';
import { useShellContext } from '../shell/context.js';
import { usePiegeFocus } from '../usePiegeFocus.js';
import { ErreurPresentation } from './ErreurPresentation.js';
import {
  cleElementPresentation,
  prixElementPresentation,
  type OeuvrePresentation,
} from './elementsPresentation.js';
import styles from './Presentation.module.css';

interface PropsRecapitulatif {
  charge: ChargePresentation;
  source: SourcePresentation;
  onRetour: () => void;
  onQuitter: () => void;
}
export function RecapitulatifPresentation(props: PropsRecapitulatif) {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'rendez-vous-client') ||
    !galerieALaction(galerie, 'presentation:read')
  )
    return null;
  return <Recapitulatif {...props} />;
}
function Recapitulatif({ charge, source, onRetour, onQuitter }: PropsRecapitulatif) {
  const { t } = useTranslation('presentation');
  const { galerieSlug, galerie } = useShellContext();
  const enLigne = useEnLigne();
  const client = useQueryClient();
  const racine = useRef<HTMLElement>(null);
  const [note, setNote] = useState('');
  const [offreOuverte, setOffreOuverte] = useState(false);
  const [reservation, setReservation] = useState<OeuvrePresentation | null>(null);
  usePiegeFocus(racine, !offreOuverte && !reservation, onQuitter);
  const recapitulatif = useQuery({
    networkMode: 'always',
    queryKey: clesPresentation.recapitulatif(galerieSlug, charge.selection.id),
    queryFn: (contexte) =>
      source.recapitulatif(galerieSlug, charge.selection.id, appelDeRequete(contexte)),
    staleTime: 0,
  });
  const noter = useMutation(
    dansLaPortee({
      mutationFn: (operation: { note: string; cleOperation?: string }, appel) => {
        operation.cleOperation ??= crypto.randomUUID();
        return source.noter(
          galerieSlug,
          charge.selection.id,
          { note: operation.note },
          { ...appel, cleOperation: operation.cleOperation },
        );
      },
      onSuccess: () => client.invalidateQueries({ queryKey: cleMetier(galerieSlug, 'contacts') }),
    }),
  );
  const affaire = useMutation(
    dansLaPortee({
      mutationFn: (
        operation: {
          contactId: string;
          lignes: { oeuvreId: string; exemplaireId: string | null; montantCentimes: number }[];
          cleOperation?: string;
        },
        appel,
      ) => {
        operation.cleOperation ??= crypto.randomUUID();
        return creerAffairePresentation(
          galerieSlug,
          { contactId: operation.contactId, lignes: operation.lignes, source: 'manuelle' },
          { ...appel, cleOperation: operation.cleOperation },
        );
      },
      onSuccess: () => client.invalidateQueries({ queryKey: cleMetier(galerieSlug, 'affaires') }),
    }),
  );
  const selection = recapitulatif.data?.selection ?? charge.selection;
  const contactId = selection.contactId;
  const marques = charge.elements.filter((element) =>
    recapitulatif.data?.interets.some(
      (interet) =>
        interet.interesse && cleElementPresentation(interet) === cleElementPresentation(element),
    ),
  );
  const actions = recapitulatif.data?.actions;
  const peutAgir = Boolean(contactId) && !selection.archivee && enLigne;
  const peutNoter =
    peutAgir &&
    actions?.notes &&
    galerieALaction(galerie, 'presentation:write') &&
    galerieALaction(galerie, 'contacts:write');
  const peutOffrir =
    peutAgir &&
    actions?.offre &&
    galerieAModule(galerie, 'offres-relances') &&
    galerieALaction(galerie, 'offers:write');
  const peutReserver =
    peutAgir && actions?.reservation && galerieALaction(galerie, 'reservations:write');
  const peutCreerAffaire =
    peutAgir &&
    actions?.affaire &&
    galerieAModule(galerie, 'pipeline-ventes') &&
    galerie.actions.some((action) => String(action) === 'pipeline:write');
  const prixRenseignes = marques.every((element) => prixElementPresentation(element) !== null);
  const exemplairesRenseignes = marques.every(
    (element) =>
      element.oeuvre.type !== 'edition' ||
      !element.oeuvre.tirage?.exemplairesSuivis ||
      element.exemplaireId !== null,
  );
  return (
    <section
      ref={racine}
      tabIndex={-1}
      className={styles.recapitulatif}
      aria-label={t('recap.titre')}
    >
      <h1>{t('recap.titre')}</h1>
      <div className={styles.actions}>
        <Button variant="secondary" onClick={onRetour}>
          {t('recap.retour')}
        </Button>
        <Button variant="secondary" onClick={onQuitter}>
          {t('mode.fermer')}
        </Button>
      </div>
      {recapitulatif.isPending ? <p role="status">{t('chargement')}</p> : null}
      <ErreurPresentation
        erreur={recapitulatif.error}
        recharger={() => void recapitulatif.refetch()}
      />
      {recapitulatif.data && !marques.length ? <p>{t('recap.vide')}</p> : null}
      {!contactId ? <p>{t('recap.contactRequis')}</p> : null}
      {!enLigne ? <p role="status">{t('calculette.reseau')}</p> : null}
      <ul className={styles.liste}>
        {marques.map((element) => (
          <li className={styles.ligne} key={cleElementPresentation(element)}>
            <span>
              {element.oeuvre.titre}
              {element.exemplaire ? ` (${element.exemplaire.numero})` : ''}
            </span>
            {peutReserver ? (
              <Button
                variant="secondary"
                disabled={
                  !transitionAutorisee(
                    element.exemplaire?.statut ?? element.oeuvre.statut,
                    'reservation',
                  ) ||
                  (element.oeuvre.tirage?.exemplairesSuivis === true && !element.exemplaireId)
                }
                onClick={() => setReservation(element)}
              >
                <Heart aria-hidden="true" size={16} />
                {t('recap.reserver', { titre: element.oeuvre.titre })}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {peutNoter ? (
        <form
          className={styles.formulaire}
          onSubmit={(evenement) => {
            evenement.preventDefault();
            noter.mutate({ note });
          }}
        >
          <Field htmlFor="recapitulatif-note" label={t('note')}>
            <Textarea
              id="recapitulatif-note"
              maxLength={5000}
              value={note}
              onChange={(evenement) => setNote(evenement.target.value)}
            />
          </Field>
          <Button type="submit" loading={noter.isPending} disabled={noter.isSuccess}>
            {t('recap.noter')}
          </Button>
        </form>
      ) : null}
      {peutCreerAffaire ? (
        <Button
          disabled={
            !marques.length || !prixRenseignes || !exemplairesRenseignes || affaire.isSuccess
          }
          loading={affaire.isPending}
          onClick={() => {
            if (!contactId || !prixRenseignes) return;
            affaire.mutate({
              contactId,
              lignes: marques.map((element) => ({
                oeuvreId: element.oeuvreId,
                exemplaireId: element.exemplaireId,
                montantCentimes: prixElementPresentation(element)!,
              })),
            });
          }}
        >
          {t('recap.affaire')}
        </Button>
      ) : null}
      {peutOffrir ? (
        <Button
          disabled={!marques.length || !prixRenseignes || !exemplairesRenseignes}
          onClick={() => setOffreOuverte(true)}
        >
          {t('recap.offre')}
        </Button>
      ) : null}
      <ErreurPresentation
        erreur={noter.error ?? affaire.error}
        recharger={() => {
          if (noter.isError && noter.variables) noter.mutate(noter.variables);
          if (affaire.isError && affaire.variables) affaire.mutate(affaire.variables);
        }}
      />
      {noter.isSuccess || affaire.isSuccess ? <p role="status">{t('succes')}</p> : null}
      {offreOuverte && peutOffrir && contactId ? (
        <NouvelleOffreDialog
          open
          onOpenChange={setOffreOuverte}
          contactInitial={{ id: contactId, nom: t('recap.contactSelection') }}
          lignesInitiales={marques.map((element) => ({
            oeuvreId: element.oeuvreId,
            titre: element.oeuvre.titre,
            prixProposeCentimes: prixElementPresentation(element)!,
            editionSuivie: element.exemplaireId !== null,
            ...(element.exemplaireId ? { exemplaireId: element.exemplaireId } : {}),
          }))}
        />
      ) : null}
      {reservation && peutReserver && contactId ? (
        <ReservationPresentation
          element={reservation}
          contactId={contactId}
          onFermer={() => setReservation(null)}
        />
      ) : null}
    </section>
  );
}

function ReservationPresentation({
  element,
  contactId,
  onFermer,
}: {
  element: OeuvrePresentation;
  contactId: string;
  onFermer: () => void;
}) {
  const { t } = useTranslation('presentation');
  const { galerieSlug } = useShellContext();
  const [jusquau, setJusquau] = useState('');
  const [dateMinimale] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const creer = useCreerReservation(galerieSlug);
  return (
    <Dialog
      open
      title={t('recap.reserver', { titre: element.oeuvre.titre })}
      description={t('recap.reservationDescription')}
      closeLabel={t('mode.fermer')}
      onOpenChange={(ouvert) => {
        if (!ouvert) onFermer();
      }}
    >
      <form
        className={styles.formulaire}
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (!jusquau) return;
          creer.mutate(
            {
              oeuvreId: element.oeuvreId,
              exemplaireId: element.exemplaireId,
              contactId,
              jusquau,
              note,
            },
            { onSuccess: onFermer },
          );
        }}
      >
        <Field htmlFor="recapitulatif-jusquau" label={t('recap.jusquau')}>
          <Input
            id="recapitulatif-jusquau"
            type="date"
            required
            min={dateMinimale}
            value={jusquau}
            onChange={(evenement) => setJusquau(evenement.target.value)}
          />
        </Field>
        <Field htmlFor="recapitulatif-reservation-note" label={t('note')}>
          <Textarea
            id="recapitulatif-reservation-note"
            maxLength={2000}
            value={note}
            onChange={(evenement) => setNote(evenement.target.value)}
          />
        </Field>
        <ErreurPresentation erreur={creer.error} />
        <Button type="submit" loading={creer.isPending}>
          {t('enregistrer')}
        </Button>
      </form>
    </Dialog>
  );
}
